import React from 'react';
import { SubscriptionType } from '../constants/types';
import { AdditionalInfo } from '../constants/styled';
import {
  CURRENT_ACTIVE_SUBSCRIPTION_MSG,
  EXPIRES_ON_MSG,
  NEXT_PAYMENT_DATE_ON_MSG,
  TO_MANAGE_APPLE_SUBSCRIPTION_USE_APP_STORE_MSG,
  SUBSCRIPTION_HOST_MSG,
  subscriptionHostLocalisation,
} from '../constants/localization';
import { format } from 'date-fns';
import styled from 'styled-components';
import { CUSTOM_DATE_FNS_FORMAT } from '../constants/ui';
import { useQuery } from 'react-apollo';
import { UNIVERSAL_USER_QUERY } from 'constants/gql/me';
import Loading from './Loading';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const RowWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-bottom: 8px;
`;

const GreenAdditionalInfo = styled(AdditionalInfo)`
  color: green;
  text-align: initial;
`;

const RedAdditionalInfo = styled(AdditionalInfo)`
  color: red;
  text-align: initial;
`;

const OrangeAdditionalInfo = styled(AdditionalInfo)`
  color: orange;
  text-align: initial;
`;

const Separator = styled.div`
  margin: 2px;
`;

const isSoonEndDate = (subscription: SubscriptionType): boolean =>
  new Date(subscription.expiredDate).getTime() - new Date().getTime() <
  7 * 24 * 60 * 60 * 1000;

const isMiddleOfDate = (subscription: SubscriptionType): boolean => {
  const diffTime: number =
    new Date(subscription.expiredDate).getTime() - new Date().getTime();
  return (
    diffTime > 7 * 24 * 60 * 60 * 1000 && diffTime < 14 * 24 * 60 * 60 * 1000
  );
};


const CurrentSubscription: React.FunctionComponent<IProps> = ({
  subscription,
  role,
  canShowExpiresOn,
}) => {

  const { data: userData, loading, error: userError } = useQuery(UNIVERSAL_USER_QUERY, {
    fetchPolicy: 'network-only',
  });

  if (userError) {
    console.error(userError);
    return <div>Error loading user data.</div>;
  }

  let subscriptionExpierationDateWithTrialDays;
  let gainedAdditionalDays: number = 0;

  if (
    loading == false && 
    userData?.profile?.manualSubscribedTillDate && 
    userData?.profile?.isManualSubscribed
  ) {
    const endSubscriptionDate = new Date(subscription.expiredDate);
    const endTrialDate = new Date(userData.profile.manualSubscribedTillDate);
    const currentDate = new Date();
    const timeDifference = endTrialDate.getTime() - currentDate.getTime();
    gainedAdditionalDays = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

    if (gainedAdditionalDays > 0) endSubscriptionDate.setDate(
      endSubscriptionDate.getDate() + gainedAdditionalDays
    );

    subscriptionExpierationDateWithTrialDays = endSubscriptionDate;
  } else {
    subscriptionExpierationDateWithTrialDays = subscription.expiredDate;
  }

  const formattedDateMessage = format(subscriptionExpierationDateWithTrialDays, CUSTOM_DATE_FNS_FORMAT);

  // Use if you want to show the gained additional days in the message
  const formatGainedDaysMessage = (): string => {
    return gainedAdditionalDays > 0
      ? ` (${format(subscription.expiredDate, CUSTOM_DATE_FNS_FORMAT)} +${gainedAdditionalDays} days from free trial)`
      : '';
  };

  return (
    loading ? <Loading /> : <Wrapper>
      {subscription.subscriptionHost === 'IOS' && (
        <RowWrapper>
          <AdditionalInfo>{CURRENT_ACTIVE_SUBSCRIPTION_MSG}</AdditionalInfo>
          <Separator />
          {subscription && subscription.status === 'Active' ? (
            <GreenAdditionalInfo>{subscription.name}</GreenAdditionalInfo>
          ) : (
            <RedAdditionalInfo>{subscription.name}</RedAdditionalInfo>
          )}
        </RowWrapper>
      )}
      {canShowExpiresOn && (
        <RowWrapper>
          <AdditionalInfo>
            {subscription && subscription.status === 'Active'
              ? NEXT_PAYMENT_DATE_ON_MSG
              : EXPIRES_ON_MSG}
          </AdditionalInfo>
          <Separator />
          {isSoonEndDate(subscription) ? (
            <RedAdditionalInfo>
              {formattedDateMessage}
            </RedAdditionalInfo>
          ) : isMiddleOfDate(subscription) ? (
            <OrangeAdditionalInfo>
              {formattedDateMessage}
            </OrangeAdditionalInfo>
          ) : (
            <GreenAdditionalInfo>
              {formattedDateMessage}
            </GreenAdditionalInfo>
          )}
        </RowWrapper>
      )}
      {role === 'PATIENT' && (
        <RowWrapper>
          <AdditionalInfo>
            {`${SUBSCRIPTION_HOST_MSG}${
              subscriptionHostLocalisation[subscription.subscriptionHost]
            }`}
          </AdditionalInfo>
        </RowWrapper>
      )}
      {subscription.subscriptionHost === 'IOS' ? (
        <RowWrapper>
          <AdditionalInfo>
            {TO_MANAGE_APPLE_SUBSCRIPTION_USE_APP_STORE_MSG}
          </AdditionalInfo>
        </RowWrapper>
      ) : null}
    </Wrapper>
  );  
};

interface IProps {
  subscription: SubscriptionType;
  cancelDisabled?: boolean;
  cancelBlockedUntilDate?: Date;
  role: string;
  canShowExpiresOn: boolean;
  isCanceled: boolean;
}

export default CurrentSubscription;
